import React from 'react';
import Button from '@/components/ui/Button';
import DelayedLoading from '@/components/ui/DelayedLoading';
import EmptyState from '@/components/ui/EmptyState';
import { SkeletonCard, SkeletonTable } from '@/components/ui/LoadingSkeleton';
import LoadingStatus from '@/components/ui/LoadingStatus';

export type BrowseResultState =
  | { status: 'loading'; message: string }
  | { status: 'error'; message: string; retry: () => void }
  | { status: 'empty'; message: string }
  | { status: 'filtered-empty'; message: string; clearFilters: () => void }
  | {
      status: 'success';
      refreshing: boolean;
      table: React.ReactNode;
      cards: React.ReactNode;
    };

export interface BrowseResultsProps {
  state: BrowseResultState;
}

const actionClasses = 'min-h-11 motion-reduce:transition-none';

const BrowseResults: React.FC<BrowseResultsProps> = ({ state }) => {
  if (state.status === 'loading') {
    return (
      <DelayedLoading loading>
        <div
          role="status"
          aria-label={state.message}
          aria-live="polite"
          className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800"
        >
          <div aria-hidden="true" className="hidden xl:block">
            <SkeletonTable label={`${state.message} table`} />
          </div>
          <div aria-hidden="true" className="space-y-3 xl:hidden">
            <SkeletonCard label={`${state.message} card`} />
            <SkeletonCard label={`${state.message} card`} />
          </div>
        </div>
      </DelayedLoading>
    );
  }

  if (state.status === 'error') {
    return (
      <EmptyState
        title={state.message}
        action={<Button className={actionClasses} onClick={state.retry}>Retry</Button>}
      />
    );
  }

  if (state.status === 'empty') {
    return <EmptyState title={state.message} />;
  }

  if (state.status === 'filtered-empty') {
    return (
      <EmptyState
        title={state.message}
        action={(
          <Button variant="outline" className={actionClasses} onClick={state.clearFilters}>
            Clear all filters
          </Button>
        )}
      />
    );
  }

  return (
    <div className="min-w-0">
      {state.refreshing && (
        <LoadingStatus variant="inline" label="Refreshing results" className="mb-3" />
      )}
      <div className="hidden min-w-0 overflow-x-auto xl:block">{state.table}</div>
      <div className="space-y-3 xl:hidden">{state.cards}</div>
    </div>
  );
};

export default BrowseResults;
